import React, { FC } from "react";
import styled from "styled-components";

import type { AOTDFormData, AOTDServerData, GenerationStatusData } from "./types";
import {
  Btn,
  FieldGroup,
  Grid,
  Input,
  Label,
  Row,
  Select,
  Textarea,
  colors,
} from "./styles";

interface Props {
  data: AOTDServerData;
  editable: Partial<AOTDFormData>;
  setEditable: React.Dispatch<React.SetStateAction<Partial<AOTDFormData>>>;
  isLoading: boolean;
  onSave: () => void;
  onDelete: () => void;
}

const Section = styled.div`
  border: 1px solid ${colors.border};
  border-radius: 10px;
  background: ${colors.bgRaised};
  padding: 14px 16px;
  margin-top: 12px;
`;

const SubHead = styled.h4`
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: ${colors.textMuted};
  margin: 0 0 10px;
`;

const STATUS_OPTIONS: GenerationStatusData["status"][] = [
  "pending",
  "generating",
  "completed",
  "failed",
  "no_art",
  "error",
  "birthday_priority",
  "started",
  "not_started",
  "resuming",
];

const STATE_OPTIONS: GenerationStatusData["state"][] = [
  "initialized",
  "generated_prompt",
  "generated_image",
  "posted_tweet",
  "posted_tg",
  "done",
  "error",
];

export const EditFields: FC<Props> = ({
  data,
  editable,
  setEditable,
  isLoading,
  onSave,
  onDelete,
}) => {
  const setField = <K extends keyof AOTDFormData>(key: K, value: AOTDFormData[K]) => {
    setEditable((prev) => ({ ...prev, [key]: value }));
  };
  const setTweetField = (key: string, value: string) => {
    setEditable((prev) => ({
      ...prev,
      tweet: { ...(prev.tweet || {}), [key]: value },
    }));
  };
  const setGsField = (key: string, value: string) => {
    setEditable((prev) => ({
      ...prev,
      generationStatus: { ...(prev.generationStatus || {}), [key]: value },
    }));
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      <Section>
        <SubHead>Identity</SubHead>
        <Grid $minColPx={280}>
          <FieldGroup>
            <Label>_id</Label>
            <Input readOnly value={data._id} />
          </FieldGroup>
          <FieldGroup>
            <Label>__v</Label>
            <Input readOnly value={String(data.__v ?? "")} />
          </FieldGroup>
          <FieldGroup>
            <Label>Date (UTC ISO)</Label>
            <Input
              value={editable.date || ""}
              onChange={(e) => setField("date", e.target.value)}
              placeholder="YYYY-MM-DDTHH:mm:ss.sssZ"
            />
          </FieldGroup>
          <FieldGroup>
            <Label>Created by</Label>
            <Input
              value={editable.createdBy || ""}
              onChange={(e) => setField("createdBy", e.target.value)}
            />
          </FieldGroup>
          <FieldGroup>
            <Label>UTC offset</Label>
            <Input
              type="number"
              min={0}
              max={12}
              value={editable.utcOffset ?? ""}
              onChange={(e) =>
                setField(
                  "utcOffset",
                  e.target.value === "" ? undefined : Number(e.target.value),
                )
              }
            />
          </FieldGroup>
          <FieldGroup>
            <Label>Quote help</Label>
            <Input
              type="number"
              value={editable.quoteHelp ?? ""}
              onChange={(e) =>
                setField(
                  "quoteHelp",
                  e.target.value === "" ? undefined : Number(e.target.value),
                )
              }
            />
          </FieldGroup>
        </Grid>
      </Section>

      <Section>
        <SubHead>Output</SubHead>
        <FieldGroup>
          <Label>Title</Label>
          <Input
            value={editable.title || ""}
            onChange={(e) => setField("title", e.target.value)}
          />
        </FieldGroup>
        <FieldGroup>
          <Label>Author quote</Label>
          <Input
            value={editable.authorQuote || ""}
            onChange={(e) => setField("authorQuote", e.target.value)}
          />
        </FieldGroup>
        <FieldGroup>
          <Label>Emojis (comma-separated)</Label>
          <Input
            value={(editable.emojis || []).join(", ")}
            onChange={(e) =>
              setField(
                "emojis",
                e.target.value
                  .split(",")
                  .map((x) => x.trim())
                  .filter(Boolean),
              )
            }
          />
        </FieldGroup>
        <FieldGroup>
          <Label>Image URL</Label>
          <Input
            value={editable.url || ""}
            onChange={(e) => setField("url", e.target.value)}
          />
        </FieldGroup>
        <FieldGroup>
          <Label>Image model</Label>
          <Input
            value={editable.imageModel || ""}
            onChange={(e) => setField("imageModel", e.target.value)}
            placeholder="e.g. dall-e-3, gpt-image-2, imagen-3"
          />
        </FieldGroup>
        <FieldGroup>
          <Label>Prompt</Label>
          <Textarea
            rows={8}
            value={editable.prompt || ""}
            onChange={(e) => setField("prompt", e.target.value)}
          />
        </FieldGroup>
      </Section>

      <Section>
        <SubHead>Tweet</SubHead>
        <Grid $minColPx={260}>
          <FieldGroup>
            <Label>Tweet ID</Label>
            <Input
              value={editable.tweet?.tweetId || ""}
              onChange={(e) => setTweetField("tweetId", e.target.value)}
            />
          </FieldGroup>
          <FieldGroup>
            <Label>Tweet URL</Label>
            <Input
              value={editable.tweet?.tweetUrl || ""}
              onChange={(e) => setTweetField("tweetUrl", e.target.value)}
            />
          </FieldGroup>
          <FieldGroup>
            <Label>Date posted (ISO)</Label>
            <Input
              value={editable.tweet?.datePosted || ""}
              onChange={(e) => setTweetField("datePosted", e.target.value)}
              placeholder="YYYY-MM-DDTHH:mm:ss.sssZ"
            />
          </FieldGroup>
        </Grid>
      </Section>

      <Section>
        <SubHead>Generation status</SubHead>
        <Grid $minColPx={260}>
          <FieldGroup>
            <Label>Status</Label>
            <Select
              value={editable.generationStatus?.status || "not_started"}
              onChange={(e) => setGsField("status", e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </FieldGroup>
          <FieldGroup>
            <Label>State</Label>
            <Select
              value={editable.generationStatus?.state || ""}
              onChange={(e) => setGsField("state", e.target.value)}
            >
              <option value="">— select —</option>
              {STATE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </FieldGroup>
          <FieldGroup>
            <Label>Started at (ISO)</Label>
            <Input
              value={editable.generationStatus?.startedAt || ""}
              onChange={(e) => setGsField("startedAt", e.target.value)}
            />
          </FieldGroup>
          <FieldGroup>
            <Label>Completed at (ISO)</Label>
            <Input
              value={editable.generationStatus?.completedAt || ""}
              onChange={(e) => setGsField("completedAt", e.target.value)}
            />
          </FieldGroup>
          <FieldGroup>
            <Label>Message</Label>
            <Input
              value={editable.generationStatus?.message || ""}
              onChange={(e) => setGsField("message", e.target.value)}
            />
          </FieldGroup>
          <FieldGroup>
            <Label>Error</Label>
            <Input
              value={editable.generationStatus?.error || ""}
              onChange={(e) => setGsField("error", e.target.value)}
            />
          </FieldGroup>
        </Grid>
      </Section>

      <Section>
        <SubHead>Views</SubHead>
        <FieldGroup>
          <Label>Total</Label>
          <Input
            type="number"
            value={editable.views?.total ?? ""}
            onChange={(e) =>
              setEditable((prev) => ({
                ...prev,
                views: {
                  viewedBy: prev.views?.viewedBy || [],
                  total: e.target.value === "" ? 0 : Number(e.target.value),
                },
              }))
            }
          />
        </FieldGroup>
        <FieldGroup>
          <Label>Viewed by</Label>
          <div
            style={{
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              padding: "8px 12px",
              fontSize: "0.85rem",
              color: colors.textMuted,
              maxHeight: 160,
              overflow: "auto",
            }}
          >
            {editable.views?.viewedBy?.length
              ? editable.views.viewedBy.join(", ")
              : "—"}
          </div>
        </FieldGroup>
      </Section>

      <Row style={{ marginTop: 16, justifyContent: "flex-end" }}>
        <Btn type="button" $variant="danger" onClick={onDelete} disabled={isLoading}>
          {isLoading ? "…" : "Delete AOTD"}
        </Btn>
        <Btn type="submit" $variant="primary" disabled={isLoading}>
          {isLoading ? "Saving…" : "Save changes"}
        </Btn>
      </Row>
    </form>
  );
};
